/**
 * Reine Auswahl-Logik für die Mehrfach-Lagerauswahl in den Kaufland-Einstellungen
 * (Migration 0322: `account.kaufland_storefronts.vario_storage_ids`).
 *
 * Bewusst DOM-frei, damit die Regeln testbar sind — Ottos Gegenstück (`syncDefaultState`
 * in `otto-settings.ts`) hängt direkt an Checkboxen und ist deshalb nur im Browser prüfbar.
 */

/**
 * Das Standard-Lager (= Vario `defaultSalesStorage` + Lager für übernommene Aufträge) muss
 * immer ein MARKIERTES Lager sein. Ist der bisherige Standard nicht mehr markiert, gilt das
 * erste markierte Lager. Ohne Auswahl gibt es keinen Standard.
 */
export function resolveDefaultSelection(selectedIds: string[], defaultId: string | null): string | null {
  if (selectedIds.length === 0) return null;
  if (defaultId && selectedIds.includes(defaultId)) return defaultId;
  return selectedIds[0] ?? null;
}

export interface ChannelReconcileResult {
  /** Auswahl nach dem Abgleich. */
  ids: string[];
  /** Standard-Lager nach dem Abgleich (kann sich verschoben haben, wenn es gestrichen wurde). */
  defaultId: string | null;
  /** Was gestrichen wurde, weil es dem Vario-Kanal nicht mehr zugeordnet ist. Leer = keine Änderung. */
  removed: string[];
}

/**
 * Gleicht die gespeicherte Lagerauswahl gegen die Lager ab, die dem Vario-Verkaufskanal
 * tatsächlich zugeordnet sind. **Vario ist führend:** entfernt der Händler dort eine Zuordnung,
 * verschwindet das Lager auch aus unserer Bestandsquelle.
 *
 * `channelIds` ist dreiwertig, und das ist die wichtigste Regel dieser Funktion:
 *   - `string[]` → echter Stand aus Vario, es wird abgeglichen
 *   - `null` / `undefined` → Zuordnung NICHT lesbar (Vario-Fehler) oder Storefront ohne Kanal
 *     → es wird NICHTS gestrichen
 *
 * Ohne diese Unterscheidung würde ein einzelner Vario-Aussetzer die gesamte Lagerauswahl leeren,
 * und der Connector meldete danach den aggregierten Bestand statt der gewählten Lager — ohne dass
 * jemand etwas geändert hat.
 */
export function reconcileWithChannel(
  savedIds: string[],
  savedDefaultId: string | null,
  channelIds: string[] | null | undefined,
): ChannelReconcileResult {
  if (!channelIds) {
    return { ids: savedIds, defaultId: resolveDefaultSelection(savedIds, savedDefaultId), removed: [] };
  }
  const allowed = new Set(channelIds);
  const ids = savedIds.filter((id) => allowed.has(id));
  const removed = savedIds.filter((id) => !allowed.has(id));
  return { ids, defaultId: resolveDefaultSelection(ids, savedDefaultId), removed };
}

/** Text im geschlossenen Dropdown. Bei einem Lager wäre „Standard: X" redundant. */
export function warehouseSummary(
  selectedIds: string[],
  defaultId: string | null,
  labelOf: (id: string) => string,
): string {
  if (selectedIds.length === 0) return '— bitte auswählen —';
  const def = resolveDefaultSelection(selectedIds, defaultId);
  if (selectedIds.length === 1) return labelOf(selectedIds[0]!);
  return `${selectedIds.length} Lager · Standard: ${def ? labelOf(def) : '—'}`;
}
